import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * <bbq-fbt> — "Frequently bought together": this item plus its complementary products, with a
 * combined total and one button that adds every ticked line.
 *
 * The block server-renders the current product so the module has content at first paint, then
 * this fetches the complementary products. Liquid's `recommendations` object is only populated in
 * a section requested through the recommendations endpoint, so a normal block cannot read it.
 *
 * IT REMOVES ITSELF WHEN THERE ARE NONE. Complementary products come from the Search & Discovery
 * app; a merchant who has configured nothing gets an empty list, and an empty "Frequently bought
 * together" is worse than no module. It deliberately does NOT fall back to `related`: related
 * products are other grills, and "frequently bought together: a second $3,000 grill" is a claim
 * the data does not support.
 *
 * ONLY SINGLE-VARIANT PRODUCTS ARE TICKABLE. A multi-variant accessory has no correct variant to
 * add without asking - adding `variants[0]` would put the wrong colour or size in someone's cart
 * silently - so those render as a link to their own page instead.
 *
 * THE CURRENT PRODUCT FOLLOWS THE VARIANT PICKER. The block is rendered with
 * `selected_or_first_available_variant`, but the shopper may pick another variant afterwards.
 * Horizon's picker dispatches `StandardEvents.productSelect` on the section with an
 * `event.promise` that resolves once the new variant is known, so this listens on its own
 * section, disables the add button the moment a selection starts, and updates the current line
 * from the resolved variant. At add time the buy form's hidden `[ref="variantId"]` input is the
 * source of truth, so a fast click after a variant change still adds the variant the shopper
 * chose, never the one the page loaded with.
 *
 * LAYOUTS (data-layout, 2026-10-17):
 *   - "inline": rendered inside the main product form on the restyled product page. No button of its
 *     own: the page's one cart button (and the sticky bar, which clicks it) adds the product and the
 *     ticked add-ons. The add-ons go in FIRST, in their own /cart/add.js, and then Horizon's normal
 *     submit adds the product - Shopify lists the newest line first, so the product sits above its
 *     cover in the cart (Henry 2026-10-17). Horizon delegates `submit` at document capture, so this
 *     listens at window capture to run before it. Nothing is pre-ticked, so the button never adds
 *     something the shopper did not choose. Outside `.bbq-pdp` (quick add, anything unstyled) the
 *     box removes itself.
 *   - "addons": configurator fireplaces. Only ticked accessories are added, never the base fireplace,
 *     and products the configurator already offers (data-exclude-config-url) are left out.
 */

class BbqFbt extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Map<string, {price: number, checkbox: HTMLInputElement | null}>} */
  #lines = new Map();

  /** The current product's line: variant id (empty when the picked combination is unavailable) and price in minor units. */
  #current = { id: '', price: 0 };

  /**
   * The latest in-flight `productSelect` promise; awaited before an add reads the variant id.
   * @type {Promise<any> | null}
   */
  #pendingSelect = null;

  get #inline() {
    return this.dataset.layout === 'inline';
  }

  get #addonsOnly() {
    return this.dataset.layout === 'addons';
  }

  async connectedCallback() {
    if (this.#inline && !this.closest('.bbq-pdp')) {
      this.remove();
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (this.#inline) window.addEventListener('submit', this.#onFormSubmit, { capture: true, signal });

    // PLAN-COVERS: products with bigapple.fbt_covers_mode = "variant" take covers from the server-rendered
    // per-variant map instead of the complementary-products fetch. Everything below this branch is the
    // legacy path, unchanged.
    if (this.dataset.mode === 'variant') {
      this.#connectVariantMode(signal);
      return;
    }

    this.#current = {
      id: String(this.dataset.variantId ?? ''),
      price: Number(this.#thisPriceEl?.dataset.price ?? 0) || 0,
    };

    // Register before the fetch so a variant picked while complementary products load is not lost.
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    const products = await this.#fetchComplementary();

    // Nothing configured, or the request failed. Leave the page as if the block did not exist.
    if (!products.length) {
      this.remove();
      return;
    }

    this.#render(products);
    this.hidden = false;

    this.addEventListener('change', this.#recalculate, { signal });
    this.refs.add?.addEventListener('click', this.#addToCart, { signal });

    this.#recalculate();
    this.#syncAddDisabled();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  get refs() {
    return {
      row: this.querySelector('[ref="row"]'),
      total: this.querySelector('[ref="total"]'),
      add: this.querySelector('[ref="add"]'),
      status: this.querySelector('[ref="status"]'),
    };
  }

  /**
   * The section that holds this product's variant picker and buy form; `productSelect` is dispatched there.
   * The block usually sits in a different section (bbq_specs, below main), so its own `.shopify-section`
   * never hears the picker and has no buy form: find the main section by product id, ignoring quick-add
   * dialogs and product cards. Falls back to the block's own section (block placed inside main).
   */
  get #section() {
    const id = this.dataset.productId;
    if (id) {
      const sel = `product-form-component[data-product-id="${id}"], variant-picker[data-product-id="${id}"]`;
      const el = [...document.querySelectorAll(sel)].find((e) => !e.closest('dialog, product-card'));
      const section = el?.closest('.shopify-section');
      if (section) return section;
    }
    return this.closest('.shopify-section, dialog');
  }

  /** @returns {HTMLElement | null} */
  get #thisPriceEl() {
    return this.querySelector('.bbq-fbt__item--this .bbq-fbt__price');
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }

  async #fetchComplementary() {
    const id = this.dataset.productId;
    const limit = Number(this.dataset.limit) || 2;
    if (!id) return [];

    try {
      const exclude = await this.#excludedIds();
      // When some will be dropped, ask for the full list (Search & Discovery caps it at 10) and trim after.
      const fetchLimit = exclude ? 10 : limit;
      const response = await fetch(
        `${window.Theme?.routes?.product_recommendations_url ?? '/recommendations/products'}.json` +
          `?product_id=${encodeURIComponent(id)}&intent=complementary&limit=${fetchLimit}`,
        { signal: this.#controller?.signal }
      );
      if (!response.ok) return [];

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      if (!exclude) return products;
      return products
        .filter(
          (p) =>
            !exclude.has(String(p.id)) &&
            !(Array.isArray(p.variants) && p.variants.some((v) => exclude.has(String(v.id))))
        )
        .slice(0, limit);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[bbq-fbt] complementary fetch failed:', error);
      return [];
    }
  }

  /**
   * Product and variant ids the fireplace configurator already offers (its `parts` and every group
   * choice), so they are not offered twice. Null when there is no config to read. If the config cannot
   * be read this throws, and the caller shows nothing rather than risk a duplicate.
   * @returns {Promise<Set<string> | null>}
   */
  async #excludedIds() {
    const url = this.dataset.excludeConfigUrl;
    if (!url) return null;
    const ids = new Set([String(this.dataset.productId ?? '')]);
    const res = await fetch(url, { signal: this.#controller?.signal });
    if (!res.ok) throw new Error(`configurator list ${res.status}`);
    const config = await res.json();
    for (const part of Object.values(config?.parts ?? {})) {
      if (part?.productId) ids.add(String(part.productId));
      if (part?.variantId) ids.add(String(part.variantId));
    }
    for (const group of config?.groups ?? []) {
      for (const choice of group?.choices ?? []) {
        if (choice?.variantId) ids.add(String(choice.variantId));
      }
    }
    return ids;
  }

  /**
   * A variant was picked in this block's section. Disable the add button at once - the resolved
   * variant is not known yet - and update the current line when the picker's promise settles.
   * @param {Event & {promise?: Promise<any>}} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const promise = event.promise;
    if (!promise) return;

    this.#pendingSelect = promise;
    const { add } = this.refs;
    if (add) add.disabled = true;

    promise
      .then(({ detail } = {}) => {
        // A newer selection superseded this one; its own handler will apply the final state.
        if (this.#pendingSelect !== promise) return;

        const { newProduct, productId, resource } = detail ?? {};
        if (newProduct?.id) {
          this.dataset.productId = String(newProduct.id);
        } else if (productId && String(productId) !== this.dataset.productId) {
          return;
        }

        if (resource?.id) {
          this.#setCurrent(String(resource.id), Number(resource.price) || 0);
        } else {
          // Unavailable combination: there is no variant to add until the shopper picks another.
          this.#setCurrent('', this.#current.price);
        }
      })
      .catch(() => {
        // An aborted or failed section fetch leaves the last known variant in place.
      })
      .finally(() => {
        if (this.#pendingSelect !== promise) return;
        this.#pendingSelect = null;
        this.#syncAddDisabled();
      });
  };

  /**
   * Re-key the current product's line and repaint its price.
   * @param {string} id
   * @param {number} price
   */
  #setCurrent(id, price) {
    const previous = this.#current.id;
    this.#current = { id, price };
    this.dataset.variantId = id;

    if (previous !== id) this.#lines.delete(previous);
    if (!this.#addonsOnly) this.#lines.set(id, { price, checkbox: null });

    const priceEl = this.#thisPriceEl;
    if (priceEl) {
      priceEl.dataset.price = String(price);
      priceEl.textContent = this.#money(price);
    }

    this.#recalculate();
  }

  /** The add button is usable only when no selection is in flight and the current variant exists. */
  #syncAddDisabled() {
    const { add } = this.refs;
    if (!add) return;
    if (this.#addonsOnly) {
      add.disabled = ![...this.#lines.values()].some((l) => l.checkbox?.checked);
      return;
    }
    add.disabled = Boolean(this.#pendingSelect) || !this.#current.id;
  }

  /**
   * The buy form's hidden variant input is what Horizon itself adds to the cart, so it is the
   * source of truth. The dataset only stands in when that input is not on the page.
   * @returns {string}
   */
  #currentVariantId() {
    const input = this.#section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"] [ref="variantId"], product-form-component [ref="variantId"]`);
    if (input instanceof HTMLInputElement) return input.value;
    return this.dataset.variantId ?? '';
  }

  /** @param {any[]} products */
  #render(products) {
    const { row } = this.refs;
    if (!row) return;

    // Seed the map with the current product, which is always part of the total - except on
    // configurator fireplaces, where only the accessories are added here.
    if (!this.#addonsOnly) this.#lines.set(this.#current.id, { price: this.#current.price, checkbox: null });

    for (const product of products) {
      // The inline and accessories-only layouts are lists without a "this item" tile, so no + signs.
      if (!this.dataset.layout) row.appendChild(this.#plus());
      row.appendChild(this.#tile(product));
    }
  }

  #plus() {
    const span = document.createElement('span');
    span.className = 'bbq-fbt__plus';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = '+';
    return span;
  }

  /** @param {any} product */
  #tile(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const single = variants.length === 1 ? variants[0] : null;

    const item = document.createElement('div');
    item.className = 'bbq-fbt__item';

    const media = document.createElement('div');
    media.className = 'bbq-fbt__media';
    if (product.featured_image) {
      const img = document.createElement('img');
      // The Ajax API returns a CDN URL; the width parameter is how it is resized.
      img.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=240`;
      img.width = 120;
      img.height = 120;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    }

    const meta = document.createElement('div');
    meta.className = 'bbq-fbt__meta';

    if (single) {
      const label = document.createElement('label');
      label.className = 'bbq-fbt__check';

      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = !this.#inline && !this.#addonsOnly;
      box.value = String(single.id);

      const text = document.createElement('span');
      text.className = 'visually-hidden';
      text.textContent = `Include ${product.title}`;

      label.append(box, text);
      meta.appendChild(label);

      this.#lines.set(String(single.id), { price: Number(single.price) || 0, checkbox: box });
    }

    const title = document.createElement('a');
    title.className = 'bbq-fbt__title';
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'bbq-fbt__price';
    price.textContent = this.#money(Number(single ? single.price : product.price) || 0);

    meta.append(title, price);

    if (!single) {
      // No single correct variant to add. Say so rather than adding the wrong one.
      const note = document.createElement('span');
      note.className = 'bbq-fbt__note';
      note.textContent = 'Choose options';
      meta.appendChild(note);
    }

    item.append(media, meta);
    return item;
  }

  #recalculate = () => {
    if (this.#inline) {
      // Add-ons are added by #onFormSubmit, never as fields of the product form.
      let n = 0;
      let addons = 0;
      for (const [, line] of this.#lines) {
        if (!line.checkbox) continue;
        line.checkbox.removeAttribute('name');
        if (line.checkbox.checked) {
          n++;
          addons += line.price;
        }
      }
      const { total: totalEl } = this.refs;
      if (totalEl) totalEl.textContent = n ? `+ ${this.#money(addons)}` : this.#money(0);
      this.toggleAttribute('data-has-addons', n > 0);
      return;
    }

    let total = 0;
    for (const [, line] of this.#lines) {
      if (!line.checkbox || line.checkbox.checked) total += line.price;
    }

    const { total: totalEl, add } = this.refs;
    if (totalEl) totalEl.textContent = this.#money(total);

    const count = [...this.#lines.values()].filter((l) => !l.checkbox || l.checkbox.checked).length;
    if (add && this.#addonsOnly) {
      add.textContent = count === 1 ? 'Add 1 accessory to cart' : `Add ${count} accessories to cart`;
      add.disabled = count === 0;
    } else if (add) {
      add.textContent = count > 1 ? `Add ${count} items to cart` : 'Add this item to cart';
    }
  };

  /** Set while the product form is re-submitted after the add-ons went in, so that submit passes through. */
  #passThrough = false;

  #addingAddons = false;

  /** Ticked add-on variant ids, read from whichever mode drew the tiles. */
  #checkedAddonIds() {
    return [...this.#lines.entries()].filter(([, l]) => l.checkbox?.checked).map(([id]) => id);
  }

  /**
   * Inline layout: the product form is being submitted (main button or sticky bar). With add-ons ticked,
   * hold Horizon's submit, add the add-ons first, then re-submit so Horizon adds the product - which then
   * lists above them in the cart. Without add-ons, or when the form cannot add, Horizon runs untouched.
   * @param {SubmitEvent} event
   */
  #onFormSubmit = async (event) => {
    const form = this.closest('form');
    if (!form || event.target !== form) return;
    if (this.#passThrough) {
      this.#passThrough = false;
      return;
    }
    if (this.#addingAddons) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const button = form.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement && button.disabled) return;
    if (!this.#checkedAddonIds().length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addingAddons = true;
    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
    const { status } = this.refs;

    try {
      // A variant change may still be redrawing which covers apply; add the ones for the final variant.
      while (this.#pending) await this.#pending.settled;
      if (this.#pendingSelect) await this.#pendingSelect.catch(() => {});

      const ids = this.#checkedAddonIds();
      if (ids.length) {
        const res = await fetch(`${window.Theme?.routes?.cart_add_url ?? '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) }),
        });
        if (!res.ok) throw new Error(`cart/add returned ${res.status}`);
      }
    } catch (error) {
      // The product still goes in; say the add-on did not.
      console.warn('[bbq-fbt] add-ons not added:', error);
      if (status) status.textContent = 'The add-on could not be added. Please add it again from the cart.';
    } finally {
      this.#addingAddons = false;
    }

    this.#passThrough = true;
    if (submitter && submitter.form === form) form.requestSubmit(submitter);
    else form.requestSubmit();
  };

  #addToCart = async () => {
    const { add, status } = this.refs;
    if (!add) return;

    add.disabled = true;
    const original = add.textContent;
    add.textContent = 'Adding…';

    try {
      // A variant change may still be resolving; wait for it so the id read below is the chosen one.
      const pending = this.#pendingSelect;
      if (pending) {
        try {
          await pending;
        } catch {
          // The last known variant stands.
        }
      }

      // Accessories-only never adds the product itself, so it needs no current variant.
      const currentId = this.#addonsOnly ? 'accessories' : this.#currentVariantId();
      if (!currentId) {
        if (status) status.textContent = 'Choose an available option before adding to cart.';
        add.textContent = original;
        return;
      }

      const items = [...this.#lines.entries()]
        .filter(([, line]) => !line.checkbox || line.checkbox.checked)
        .map(([id, line]) => ({ id: Number(line.checkbox ? id : currentId), quantity: 1 }));

      if (!items.length) {
        add.textContent = original;
        return;
      }

      const res = await fetch(`${window.Theme?.routes?.cart_add_url ?? '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) throw new Error(`cart/add returned ${res.status}`);

      // `/cart/add.js` returns the added lines, not the cart, and the cart icon needs a total.
      // The add has succeeded at this point, so a failed refresh must not read as a failed add:
      // the count update is skipped and the add is still announced.
      let itemCount = null;
      try {
        const cartRes = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`);
        if (cartRes.ok) {
          const cart = await cartRes.json();
          if (Number.isFinite(cart?.item_count)) itemCount = cart.item_count;
        }
      } catch (error) {
        console.warn('[bbq-fbt] cart refresh failed after add:', error);
      }

      if (itemCount !== null) {
        // Announce through the same event the cart icon and drawer already listen for, so this
        // module does not have to know how either of them re-renders.
        const event = new Event(StandardEvents.cartLinesUpdate, { bubbles: true });
        // @ts-expect-error - the theme's listeners read `.promise` off the event.
        event.promise = Promise.resolve({ cart: null, detail: { itemCount } });
        document.dispatchEvent(event);
      }

      window.Shopify?.actions?.openCart?.perform?.();

      if (status) status.textContent = `${items.length} item${items.length === 1 ? '' : 's'} added to your cart.`;
      add.textContent = 'Added';
    } catch (error) {
      console.warn('[bbq-fbt] add to cart failed:', error);
      if (status) status.textContent = 'Could not add to cart. Please try again.';
      add.textContent = original;
    } finally {
      this.#syncAddDisabled();
    }
  };

  /* ------------------------------------------------------------------------------------------------
   * MODE "variant" (PLAN-COVERS, theme 1a)
   *
   * The block's <script data-fbt-covers> carries `covers` (cover variant id -> display data, already
   * filtered to available variants) and `variants` (grill variant id -> {covers: [ranked ids], bundled}).
   * All ids are decimal strings. The shopper's selected grill variant decides which covers show:
   *   - one COMMITTED state {generation, variantId, covers, checked, bundled} is the only thing Add reads;
   *   - every productSelect bumps `generation` and disables Add until its promise settles;
   *   - a superseded selection's result (or AbortError) is ignored; a failure of the CURRENT one
   *     invalidates the state (variantId '') so nothing can be added until a later selection resolves;
   *   - bundled (the variant already includes a cover, D6) or no covers -> the block is hidden, not removed,
   *     so it comes back for a later variant that has covers;
   *   - covers[0] is pre-ticked, the second is not (D12).
   * ---------------------------------------------------------------------------------------------- */

  /** @type {{covers: Record<string, any>, variants: Record<string, {covers?: string[], bundled?: boolean}>}} */
  #payload = { covers: {}, variants: {} };

  /** Incremented by every productSelect for this product. */
  #generation = 0;

  /** @type {{generation: number, variantId: string, price: number, covers: any[], checked: Set<string>, bundled: boolean}} */
  #state = { generation: 0, variantId: '', price: 0, covers: [], checked: new Set(), bundled: false };

  /** @type {{generation: number, settled: Promise<void>} | null} */
  #pending = null;

  #adding = false;

  /** @param {AbortSignal} signal */
  #connectVariantMode(signal) {
    this.#payload = this.#readPayload();

    // Capture phase on document: survives the main section being morphed or replaced, and sees the event
    // even when the block lives in a different section from the picker.
    document.addEventListener(StandardEvents.productSelect, this.#onVariantModeSelect, { capture: true, signal });
    this.addEventListener('change', this.#onCoverToggle, { signal });
    this.refs.add?.addEventListener('click', this.#addVariantMode, { signal });

    const price = Number(this.#thisPriceEl?.dataset.price ?? 0) || 0;
    this.#commit(0, String(this.dataset.variantId ?? ''), price);
  }

  #readPayload() {
    const empty = { covers: {}, variants: {} };
    const script = this.querySelector('script[data-fbt-covers]');
    if (!script?.textContent) return empty;
    try {
      const data = JSON.parse(script.textContent);
      return {
        covers: data?.covers && typeof data.covers === 'object' ? data.covers : {},
        variants: data?.variants && typeof data.variants === 'object' ? data.variants : {},
      };
    } catch (error) {
      console.warn('[bbq-fbt] cover payload unreadable:', error);
      return empty;
    }
  }

  /** Covers for a grill variant: ranked, available, deduplicated, max 2; none when bundled or unknown. */
  #coversFor(variantId) {
    const entry = variantId ? this.#payload.variants[variantId] : null;
    if (!entry || entry.bundled === true || !Array.isArray(entry.covers)) return [];
    const seen = new Set();
    const out = [];
    for (const raw of entry.covers) {
      const id = String(raw);
      const cover = this.#payload.covers[id];
      if (!cover || seen.has(id)) continue;
      seen.add(id);
      out.push(cover);
      if (out.length === 2) break;
    }
    return out;
  }

  /**
   * Replace the committed state and repaint. `variantId` '' = invalid (unavailable combination or a
   * failed selection): no covers, nothing addable.
   */
  #commit(generation, variantId, price) {
    const entry = variantId ? this.#payload.variants[variantId] : null;
    const bundled = entry?.bundled === true;
    const covers = this.#coversFor(variantId);
    const checked = new Set(covers.length && !this.#inline ? [String(covers[0].id)] : []);
    this.#state = { generation, variantId, price, covers, checked, bundled };
    this.dataset.variantId = variantId;
    this.#renderVariantMode();
  }

  #renderVariantMode() {
    const { row } = this.refs;
    const state = this.#state;

    // Drop the previous variant's cover tiles and lines.
    row?.querySelectorAll('[data-fbt-cover-tile], [data-fbt-cover-plus]').forEach((el) => el.remove());
    this.#lines.clear();

    const priceEl = this.#thisPriceEl;
    if (priceEl && state.variantId) {
      priceEl.dataset.price = String(state.price);
      priceEl.textContent = this.#money(state.price);
    }

    if (!state.variantId || state.bundled || !state.covers.length) {
      this.hidden = true;
      this.#syncVariantAdd();
      return;
    }

    this.#lines.set(state.variantId, { price: state.price, checkbox: null });
    for (const cover of state.covers) {
      if (!this.dataset.layout) {
        const plus = this.#plus();
        plus.dataset.fbtCoverPlus = '';
        row?.appendChild(plus);
      }
      row?.appendChild(this.#coverTile(cover, state.checked.has(String(cover.id))));
    }

    this.hidden = false;
    this.#recalculate();
    this.#syncVariantAdd();
  }

  /** Cover title plus its non-default variant title (size / colour). */
  #coverLabel(cover) {
    const title = String(cover.title ?? '');
    return cover.variant_title ? `${title} - ${cover.variant_title}` : title;
  }

  /**
   * A cover tile. Always tickable: it references one exact cover variant (D7).
   * @param {any} cover
   * @param {boolean} checked
   */
  #coverTile(cover, checked) {
    const id = String(cover.id);
    const label = this.#coverLabel(cover);

    const item = document.createElement('div');
    item.className = 'bbq-fbt__item';
    item.dataset.fbtCoverTile = id;

    const media = document.createElement('div');
    media.className = 'bbq-fbt__media';
    if (cover.image) {
      const img = document.createElement('img');
      img.src = String(cover.image);
      img.width = 120;
      img.height = 120;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    }

    const meta = document.createElement('div');
    meta.className = 'bbq-fbt__meta';

    const check = document.createElement('label');
    check.className = 'bbq-fbt__check';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = checked;
    box.value = id;
    box.dataset.fbtCover = id;
    const text = document.createElement('span');
    text.className = 'visually-hidden';
    text.textContent = `Include ${label}`;
    check.append(box, text);

    const title = document.createElement('a');
    title.className = 'bbq-fbt__title';
    title.href = String(cover.url ?? '#');
    title.textContent = label;

    const price = document.createElement('span');
    price.className = 'bbq-fbt__price';
    price.textContent = this.#money(Number(cover.price) || 0);

    meta.append(check, title, price);
    item.append(media, meta);

    this.#lines.set(id, { price: Number(cover.price) || 0, checkbox: box });
    return item;
  }

  /** @param {Event} event */
  #onCoverToggle = (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || box.dataset.fbtCover === undefined) return;
    if (box.checked) this.#state.checked.add(box.value);
    else this.#state.checked.delete(box.value);
    this.#recalculate();
  };

  #syncVariantAdd() {
    const { add } = this.refs;
    if (add) add.disabled = this.#adding || Boolean(this.#pending) || !this.#state.variantId;
  }

  /** The main product's buy form (not a quick-add dialog or a product card), re-queried every time. */
  #mainBuyForm() {
    const id = this.dataset.productId;
    if (!id) return null;
    return (
      [...document.querySelectorAll(`product-form-component[data-product-id="${id}"]`)].find(
        (el) => !el.closest('dialog, product-card')
      ) ?? null
    );
  }

  /** @param {Event & {promise?: Promise<any>}} event */
  #onVariantModeSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('dialog, product-card')) return;
    const owner = target.closest('[data-product-id]');
    if (!(owner instanceof HTMLElement) || owner.dataset.productId !== this.dataset.productId) return;
    const promise = event.promise;
    if (!promise) return;

    const generation = ++this.#generation;
    const settled = Promise.resolve(promise)
      .then(
        ({ detail } = {}) => {
          if (generation !== this.#generation) return; // superseded
          const { newProduct, resource } = detail ?? {};
          if (newProduct?.id && String(newProduct.id) !== this.dataset.productId) {
            // Another product (combined listing): this block's map does not describe it.
            this.#commit(generation, '', 0);
          } else if (resource?.id) {
            this.#commit(generation, String(resource.id), Number(resource.price) || 0);
          } else {
            this.#commit(generation, '', 0); // unavailable combination
          }
        },
        (error) => {
          if (generation !== this.#generation) return; // superseded (normally an AbortError)
          console.warn('[bbq-fbt] variant selection failed:', error);
          this.#commit(generation, '', 0);
        }
      )
      .finally(() => {
        if (this.#pending?.generation === generation) this.#pending = null;
        this.#syncVariantAdd();
      });

    this.#pending = { generation, settled };
    this.#syncVariantAdd();
  };

  #mainQuantity() {
    const input = this.#mainBuyForm()?.querySelector('input[name="quantity"]');
    const qty = input instanceof HTMLInputElement ? parseInt(input.value, 10) : NaN;
    return Number.isFinite(qty) && qty >= 1 ? qty : 1;
  }

  /**
   * @param {{productId: string, variantId: string, coverIds: string[], status: string | number}} detail
   */
  #reportAddFailed(detail) {
    console.warn('[bbq-fbt] add to cart failed:', detail);
    this.dispatchEvent(new CustomEvent('bbq-fbt:add-failed', { bubbles: true, detail }));
  }

  #addVariantMode = async () => {
    const { add, status } = this.refs;
    if (!add || this.#adding) return;

    const snapshot = this.#generation;
    this.#adding = true;
    this.#syncVariantAdd();
    const original = add.textContent;
    add.textContent = 'Adding…';
    const say = (text) => {
      if (status) status.textContent = text;
    };

    let state = this.#state;
    let coverIds = [...state.checked];
    try {
      // Wait out every in-flight selection, including ones that start while we wait.
      while (this.#pending) await this.#pending.settled;

      if (this.#generation !== snapshot) {
        this.#renderVariantMode();
        say('Options changed — please review');
        this.#reportAddFailed({
          productId: String(this.dataset.productId ?? ''),
          variantId: this.#state.variantId,
          coverIds: [...this.#state.checked],
          status: 'options-changed',
        });
        add.textContent = original;
        return;
      }

      // One committed state supplies the grill AND the covers.
      state = this.#state;
      coverIds = state.covers.map((c) => String(c.id)).filter((id) => state.checked.has(id));

      if (!state.variantId) {
        say('Choose an available option before adding to cart.');
        add.textContent = original;
        return;
      }

      const input = this.#mainBuyForm()?.querySelector('[ref="variantId"]');
      if (input instanceof HTMLInputElement && String(input.value) !== state.variantId) {
        say('Options changed — please review');
        this.#reportAddFailed({
          productId: String(this.dataset.productId ?? ''),
          variantId: state.variantId,
          coverIds,
          status: 'variant-mismatch',
        });
        add.textContent = original;
        return;
      }

      const items = [
        { id: Number(state.variantId), quantity: this.#mainQuantity() },
        ...coverIds.map((id) => ({ id: Number(id), quantity: 1 })),
      ];

      let failure = null;
      try {
        const res = await fetch(`${window.Theme?.routes?.cart_add_url ?? '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) failure = res.status;
      } catch (error) {
        failure = error?.name || 'network-error';
      }

      // Either way the cart is re-read: after a failure it says which lines actually landed.
      let cart = null;
      try {
        const cartRes = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`);
        if (cartRes.ok) cart = await cartRes.json();
      } catch (error) {
        console.warn('[bbq-fbt] cart refresh failed after add:', error);
      }

      if (cart && Number.isFinite(cart.item_count)) {
        const event = new Event(StandardEvents.cartLinesUpdate, { bubbles: true });
        // @ts-expect-error - the theme's listeners read `.promise` off the event.
        event.promise = Promise.resolve({ cart: null, detail: { itemCount: cart.item_count } });
        document.dispatchEvent(event);
      }

      if (failure !== null) {
        this.#reportAddFailed({
          productId: String(this.dataset.productId ?? ''),
          variantId: state.variantId,
          coverIds,
          status: failure,
        });
        const names = new Map([[state.variantId, this.querySelector('.bbq-fbt__item--this .bbq-fbt__title')?.textContent?.trim() || 'This item']]);
        for (const cover of state.covers) names.set(String(cover.id), this.#coverLabel(cover));
        if (cart && Array.isArray(cart.items)) {
          const inCart = new Set(cart.items.map((line) => String(line.variant_id)));
          const intended = items.map((i) => String(i.id));
          const landed = intended.filter((id) => inCart.has(id));
          const missing = intended.filter((id) => !inCart.has(id));
          say(
            landed.length
              ? `Not everything was added. In your cart: ${landed.map((id) => names.get(id)).join(', ')}. Not added: ${missing.map((id) => names.get(id)).join(', ')}.`
              : 'Could not add to cart. Please try again.'
          );
          this.dataset.lastAddLanded = landed.join(',');
          this.dataset.lastAddMissing = missing.join(',');
        } else {
          say('Could not add to cart. Please check your cart and try again.');
        }
        add.textContent = original;
        return;
      }

      window.Shopify?.actions?.openCart?.perform?.();
      say(`${items.length} item${items.length === 1 ? '' : 's'} added to your cart.`);
      add.textContent = 'Added';
    } catch (error) {
      this.#reportAddFailed({
        productId: String(this.dataset.productId ?? ''),
        variantId: state.variantId,
        coverIds,
        status: error?.name || 'error',
      });
      say('Could not add to cart. Please try again.');
      add.textContent = original;
    } finally {
      this.#adding = false;
      this.#syncVariantAdd();
    }
  };
}

if (!customElements.get('bbq-fbt')) {
  customElements.define('bbq-fbt', BbqFbt);
}
